import React, { useEffect, useState } from "react";
import { Check, X } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Container,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { School, Group } from "@mui/icons-material";
import googlePlayImg from "../../assets/img/mobile_app/get_it_on_google_play.png";
import appStoreImg from "../../assets/img/mobile_app/download_on_the_app_store.png";
import { GOOGLE_PLAY_APP, APP_STORE_APP } from "../../constants/constants";
import { useTranslation } from "react-i18next";
import { LOGIN_ROUTE } from "../../routes/RoutesPathes";
import { BASE_URL } from "../../constants/apiConstants";
import { LoadingDots } from "../../components/general/LoadingDots";
import BookDemoModal from "../../components/components/js/BookDemoModal";

export const Pricing = () => {
  const { t, i18n } = useTranslation();
  const direction = i18n.dir();
  const [openDemo, setOpenDemo] = useState(false);
  const [pricing, setPricing] = useState();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchPrice = () => {
      setLoading(true);
      fetch(`${BASE_URL}/api/user-country/`)
        .then((res) => res.json())
        .then((data) => {
          console.log("User country:", data);
          setPricing(data);
          setLoading(false);
        });
    };
    fetchPrice();
  }, []);

  return loading ? (
    <LoadingDots />
  ) : (
    <Container maxWidth="lg" sx={{ py: 10 }}>
      <Box textAlign="center" mb={10}>
        <Typography variant="h2" fontWeight="bold" gutterBottom>
          {t("pricing.title")}
        </Typography>
        <Typography variant="h6" color="text.secondary">
          {t("pricing.subtitle")}
        </Typography>
        <Box
          mt={4}
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="center"
          gap={2}
        >
          <Button
            variant="contained"
            color="primary"
            size="large"
            sx={{ px: 4 }}
            onClick={() =>
              window.open(LOGIN_ROUTE, "_blank", "noopener,noreferrer")
            }
          >
            {t("pricing.tryPremium")}
          </Button>
          <Button
            component={RouterLink}
            variant="outlined"
            size="large"
            sx={{ px: 4 }}
            onClick={() => {
              window.scrollTo({
                top: document.body.scrollHeight,
                behavior: "smooth",
              });
            }}
          >
            {t("pricing.downloadApp")}
          </Button>
        </Box>
      </Box>


      {/* Pricing Table */}
      <Box
        component={Paper}
        mb={10}
        elevation={3}
        sx={{
          overflowX: "auto",
          p: 4,
          borderRadius: 4,
          backgroundColor: "background.paper",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="h4" fontWeight="bold" align="center" gutterBottom>
          {t("pricing.dashboardPricing")}
        </Typography>
        <Typography variant="body2" align="center" color="text.secondary">
          {t("pricing.allPricesUSD")}
        </Typography>
        <Grid container spacing={4} justifyContent="center" mt={2}>
          <Grid item xs={12} sm={6}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.200",
                position: "relative",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 6px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                  gutterBottom
                >
                  <School sx={{ verticalAlign: "middle", mr: 1 }} />
                  {t("pricing.schoolPricingTitle")}
                </Typography>
                <Typography variant="h3" color="primary" fontWeight="bold">
                  {pricing?.price ?? "-"} {pricing?.currency ?? ""}
                  <Typography
                    component="span"
                    variant="h6"
                    color="text.secondary"
                  >
                    {t("pricing.schoolPricingSubtitle")}
                  </Typography>
                </Typography>
                <Typography mt={1} color="text.secondary">
                  {t("pricing.schoolPricingNote")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card
              elevation={4}
              sx={{
                borderRadius: 4,
                border: "1px solid",
                borderColor: "grey.200",
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0px 6px 24px rgba(0,0,0,0.1)",
                },
              }}
            >
              <CardContent>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  color="text.secondary"
                  gutterBottom
                >
                  <Group sx={{ verticalAlign: "middle", mr: 1 }} />
                  {t("pricing.largeSchoolPricingTitle")}
                </Typography>
                <Typography variant="h3" fontWeight="bold" color="primary">
                  {t("pricing.largeSchoolPricingSubtitle")}
                </Typography>
                <Typography mt={1} color="text.secondary">
                  {t("pricing.largeSchoolPricingNote")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        <Typography
          variant="body2"
          color="text.secondary"
          align="center"
          mt={2}
        >
          {t("pricing.billingNote")}
        </Typography>
      </Box>

      <Box
        component={Paper}
        elevation={3}
        mb={10}
        textAlign="center"
        sx={{
          overflowX: "auto",
          p: 4,
          borderRadius: 4,
          backgroundColor: "background.paper",
          boxShadow: "0px 4px 20px rgba(0,0,0,0.06)",
        }}
      >
        <Typography variant="h4" fontWeight="bold">
          {t("pricing.inclusiveTitle")}
        </Typography>
        <Typography maxWidth="md" mx="auto" mt={2} sx={{ fontSize: "1.2rem" }}>
          {t("pricing.inclusiveDescription")}
          <br />
          {t("pricing.inclusiveDescriptionQuote")}
        </Typography>
      </Box>

      {/* Contact CTA Section */}
      <Box textAlign="center" mb={10}>
        <Typography variant="h6">📧 schools@sabeqoon.com</Typography>
        <Box
          mt={2}
          display="flex"
          flexDirection={{ xs: "column", md: "row" }}
          justifyContent="center"
          gap={2}
        >
          <Button variant="contained" onClick={() => setOpenDemo(true)}>
            {t("pricing.bookDemo")}
          </Button>
          <a href="mailto:schools@sabeqoon.com">
            <Button variant="outlined">{t("pricing.customOffer")}</Button>
          </a>
        </Box>
      </Box>

      {/* Download Section */}
      <Box textAlign="center">
        <Typography variant="h6">{t("pricing.downloadAppNow")}</Typography>
        <Box mt={2} display="flex" justifyContent="center" gap={2}>
          <a href={GOOGLE_PLAY_APP} aria-label="Download on Google Play">
            <img src={googlePlayImg} alt="Get it on Google Play" height={48} />
          </a>
          <a href={APP_STORE_APP} aria-label="Download on App Store">
            <img
              src={appStoreImg}
              alt="Download on the App Store"
              height={48}
            />
          </a>
        </Box>
        <Typography variant="body2" color="text.secondary" mt={2}>
          {t("pricing.secureBillingNote")}
        </Typography>
      </Box>
      <BookDemoModal 
        open={openDemo} 
        onClose={() => setOpenDemo(false)} 
      />
    </Container>
  );
};
